import { ICustomer } from '../../../../../../../app/entities/Customer'
import { ICustomerVehicle } from '../../../../../../../app/entities/CustomerVehicle'
import { IInstallationPoint } from '../../../../../../../app/entities/InstallationPoint'
import { IOrderVehicle } from '../../../../../../../app/entities/OrderVehicle'
import { fetchData } from '../../../../../global/utils/fetch'
import {
  isValidEmail,
  isTenDigitPhoneNumber,
} from '../../../../../global/utils/formValidation'
import { ISchedulerStateProps } from '../container-modal/initialData'
import { ICustomerInformationData } from './CustomerInformation'
import {
  updateCustomer,
  updateCustomerVehicle,
  createCustomerVehicle,
  setCustomerVehicle,
  createInstallationPoint,
  setInstallationPoint,
  updateInstallationPoint,
} from './api'
import { Dispatch, SetStateAction } from 'react'
import { Address } from '../../../../../../../app/types/scheduling'

export function checkFormFields(
  formData: ICustomerInformationData,
  isBlacklisted: boolean,
  shouldIgnoreContactInfo = false,
): boolean {
  // Fields that can be empty
  const optionalFields: Array<keyof ICustomerInformationData> = [
    'additionalInformation',
    'aptOrSuite',
    'brandId',
    'installationPointObjectId',
    'mileage',
  ]

  // Check all required fields are non-empty
  for (const key in formData) {
    const typedKey = key as keyof ICustomerInformationData
    const value = formData[typedKey]

    // Skip optional fields
    if (optionalFields.includes(typedKey)) {
      continue
    }

    // Check for empty string values
    if (value === '') {
      return false
    }
  }

  // Validate contact info if not ignored
  if (!shouldIgnoreContactInfo) {
    if (!isValidEmail(formData.email)) {
      return false
    }
    if (!isTenDigitPhoneNumber(formData.phone)) {
      return false
    }
  }

  return !isBlacklisted
}

const updateCustomerPromise = async (
  formData: ICustomerInformationData,
  custId: string,
) => {
  const body = {
    objectId: custId,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phoneNumber: formData.phone,
  }

  try {
    await fetchData<ICustomer>(updateCustomer(body))
  } catch (error) {
    throw error
  }
}

const updateCustomerVehiclePromise = async (
  formData: ICustomerInformationData,
  customerVehicleId: string,
  orderVehicleId: string,
) => {
  const body = {
    brand: {
      name: formData.brandName,
      objectId: formData.brandId,
    },
    customerVehicleId: customerVehicleId,
    model: formData.model,
    objectId: customerVehicleId,
    orderVehicleId: orderVehicleId,
    variant: formData.variant,
    product: null,
    year: formData.year,
  }

  try {
    await fetchData<ICustomerVehicle>(updateCustomerVehicle(body))
  } catch (error) {
    throw error
  }
}
const createCustomerVehiclePromise = async (
  formData: ICustomerInformationData,
  customerId: string,
) => {
  const body = {
    variant: formData.variant,
    year: formData.year,
    model: formData.model,
    brand: {
      objectId: formData.brandId,
      name: formData.brandName,
    },
  }
  try {
    const res = await fetchData<ICustomerVehicle>(
      createCustomerVehicle(body, customerId),
    )
    return res
  } catch (error) {
    throw error
  }
}
const setCustomerVehiclePromise = async (
  orderId: string,
  vehicleObjectId: string,
) => {
  try {
    await fetchData<IOrderVehicle[]>(
      setCustomerVehicle(orderId, vehicleObjectId),
    )
  } catch (error) {
    throw error
  }
}

const createInstallationPointPromise = async (
  formData: ICustomerInformationData,
  orderId: string,
) => {
  const body = {
    zipCode: formData.zipCode,
    address: formData.address,
    additionalInformation: formData.additionalInformation,
    aptOrSuite: formData.aptOrSuite,
    city: formData.city,
    state: formData.state,
  }

  try {
    const installationPoint = await fetchData<IInstallationPoint>(
      createInstallationPoint(body),
    )
    await fetchData<IInstallationPoint>(
      setInstallationPoint(installationPoint, orderId),
    )
  } catch (error) {
    throw error
  }
}
export const updateInstallationPointPromise = async (
  formData: ICustomerInformationData,
) => {
  const body = {
    zipCode: formData.zipCode,
    address: formData.address,
    additionalInformation: formData.additionalInformation,
    aptOrSuite: formData.aptOrSuite,
    city: formData.city,
    state: formData.state,
    objectId: formData.installationPointObjectId,
  }

  try {
    await fetchData<IInstallationPoint>(updateInstallationPoint(body))
  } catch (error) {
    throw error
  }
}

export async function handleWithoutCustomerVehicleId(
  customerId: string,
  formData: ICustomerInformationData,
  orderId: string,
  setSchedulerState: Dispatch<SetStateAction<ISchedulerStateProps>>,
  shouldSaveCustomer: boolean,
) {
  try {
    const createCustomerVehicleResponse = await createCustomerVehiclePromise(
      formData,
      customerId,
    )
    setSchedulerState((prevState: ISchedulerStateProps) => ({
      ...prevState,
      orderVehicleId: createCustomerVehicleResponse.objectId,
    }))

    const promiseArray = [
      setCustomerVehiclePromise(
        orderId,
        createCustomerVehicleResponse.objectId,
      ),
    ]

    if (shouldSaveCustomer) {
      promiseArray.push(updateCustomerPromise(formData, customerId))
    }

    if (formData.installationPointObjectId) {
      // this should go away since we have a different flow for updating installation point
      promiseArray.push(updateInstallationPointPromise(formData))
    } else {
      promiseArray.push(createInstallationPointPromise(formData, orderId))
    }
    await Promise.all(promiseArray)
  } catch (error) {
    throw error
  }
}

export async function handleWithCustomerVehicleId(
  customerId: string,
  customerVehicleId: string,
  formData: ICustomerInformationData,
  orderId: string,
  orderVehicleId: string,
  shouldSaveCustomer: boolean,
) {
  try {
    const promiseArray = [
      updateCustomerVehiclePromise(formData, customerVehicleId, orderVehicleId),
    ]

    if (shouldSaveCustomer) {
      promiseArray.push(updateCustomerPromise(formData, customerId))
    }

    if (formData.installationPointObjectId) {
      // this should go away since we have a different flow for updating installation point
      promiseArray.push(updateInstallationPointPromise(formData))
    } else {
      promiseArray.push(createInstallationPointPromise(formData, orderId))
    }

    await Promise.all(promiseArray)
  } catch (error) {
    throw error
  }
}
